<template>
  <v-dialog :model-value="modelValue" max-width="1200" scrollable @update:model-value="emit('update:modelValue', $event)">
    <v-card>
      <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px;">
        <v-icon class="mr-1">mdi-history</v-icon>
        <span>{{ t('admConfig.skillHistory.title') }}</span>
        <code class="text-body-2">{{ skillId }}.md</code>
        <v-spacer />
        <v-btn icon variant="text" :loading="loading" :title="t('admConfig.skillsEditor.reload')" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon variant="text" :disabled="restoring" @click="emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="max-height: 75vh;">
        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
          {{ error }}
        </v-alert>
        <v-alert v-if="restoredMessage" type="success" variant="tonal" density="compact" closable class="mb-3"
                 @click:close="restoredMessage = ''">
          {{ restoredMessage }}
        </v-alert>

        <div v-if="loading && !versions.length" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else class="history-layout">
          <!-- Elenco: la versione corrente, poi quelle archiviate dalla più recente -->
          <div class="history-list">
            <div class="text-caption text-medium-emphasis mb-2">
              {{ t('admConfig.skillHistory.current', { date: formatDateTime(currentModifiedAt) }) }}
            </div>
            <div v-if="!versions.length" class="text-body-2 text-medium-emphasis py-4">
              {{ t('admConfig.skillHistory.empty') }}
            </div>
            <v-list v-else density="compact" class="py-0">
              <v-list-item
                v-for="version in versions"
                :key="version.id"
                :active="selected?.id === version.id"
                color="primary"
                rounded
                @click="select(version)"
              >
                <v-list-item-title class="text-body-2">
                  {{ t('admConfig.skillHistory.replacedAt', { date: formatDateTime(version.savedAt) }) }}
                </v-list-item-title>
                <v-list-item-subtitle class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <v-chip size="x-small" label variant="tonal" :color="originColor(version.origin)">
                    {{ t(`admConfig.skillHistory.origin_${version.origin}`) }}
                  </v-chip>
                  <span v-if="version.savedBy">{{ t('admConfig.skillHistory.writtenBy', { user: version.savedBy }) }}</span>
                  <span>· {{ ((version.length || 0) / 1024).toFixed(1) }} KB</span>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <div class="text-caption text-medium-emphasis mt-3">
              {{ t('admConfig.skillHistory.retentionHint') }}
            </div>
          </div>

          <!-- Testo della versione scelta, sola lettura -->
          <div class="history-preview">
            <div v-if="!selected" class="text-body-2 text-medium-emphasis py-4">
              {{ t('admConfig.skillHistory.selectHint') }}
            </div>
            <div v-else-if="previewLoading" class="d-flex justify-center py-8">
              <v-progress-circular indeterminate color="primary" />
            </div>
            <v-textarea
              v-else
              :model-value="preview"
              rows="24"
              density="compact"
              class="skill-source"
              readonly
              hide-details
            />
          </div>
        </div>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <span class="text-caption text-medium-emphasis ml-2">
          {{ t('admConfig.skillHistory.restoreHint') }}
        </span>
        <v-spacer />
        <v-btn
          variant="text"
          prepend-icon="mdi-download"
          :disabled="!selected || previewLoading || !preview"
          @click="download"
        >
          {{ t('admConfig.skillHistory.download') }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          prepend-icon="mdi-restore"
          :loading="restoring"
          :disabled="!selected || previewLoading"
          @click="confirmDialog = true"
        >
          {{ t('admConfig.skillHistory.restore') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="confirmDialog" max-width="480">
      <v-card>
        <v-card-title>{{ t('admConfig.skillHistory.restoreConfirmTitle') }}</v-card-title>
        <v-card-text>
          {{ t('admConfig.skillHistory.restoreConfirmText', { date: formatDateTime(selected?.savedAt) }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmDialog = false">{{ t('common.cancel') }}</v-btn>
          <v-btn variant="flat" color="primary" @click="restore">{{ t('admConfig.skillHistory.restore') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import { asDate } from '../utils/aiRuns.js'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  skillId: { type: String, default: '' }
})
const emit = defineEmits(['update:modelValue', 'restored'])

const { t } = useI18n()
const projectStore = useProjectStore()

const versions = ref([])
const currentModifiedAt = ref(null)
const loading = ref(false)
const error = ref('')

const selected = ref(null)
const preview = ref('')
const previewLoading = ref(false)

const confirmDialog = ref(false)
const restoring = ref(false)
const restoredMessage = ref('')

const load = async () => {
  if (!props.skillId) return
  loading.value = true
  error.value = ''
  try {
    const result = await projectStore.loadSkillHistory(props.skillId)
    versions.value = result?.versions || []
    currentModifiedAt.value = result?.current?.modifiedAt || null
    // La versione scelta può essere uscita dalla ritenzione dopo un ripristino
    if (selected.value && !versions.value.some(v => v.id === selected.value.id)) {
      selected.value = null
      preview.value = ''
    }
  } catch (e) {
    error.value = e?.message || t('admConfig.skillHistory.loadError')
  } finally {
    loading.value = false
  }
}

const select = async (version) => {
  selected.value = version
  preview.value = ''
  previewLoading.value = true
  error.value = ''
  try {
    const result = await projectStore.loadSkillVersion(props.skillId, version.id)
    // Risposta di una scelta precedente arrivata in ritardo: la si scarta
    if (selected.value?.id === version.id) preview.value = result?.content || ''
  } catch (e) {
    error.value = e?.message || t('admConfig.skillHistory.loadError')
  } finally {
    if (selected.value?.id === version.id) previewLoading.value = false
  }
}

const restore = async () => {
  confirmDialog.value = false
  if (!selected.value) return
  restoring.value = true
  error.value = ''
  restoredMessage.value = ''
  try {
    await projectStore.restoreSkillVersion(props.skillId, selected.value.id)
    restoredMessage.value = t('admConfig.skillHistory.restored')
    emit('restored')
    await load()
  } catch (e) {
    error.value = e?.message || t('admConfig.skillHistory.restoreError')
  } finally {
    restoring.value = false
  }
}

const download = () => {
  if (!selected.value || !preview.value) return
  const blob = new Blob([preview.value], { type: 'text/markdown' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${selected.value.label || `${props.skillId}-${selected.value.id}`}.md`
  a.click()
  URL.revokeObjectURL(url)
}

const originColor = origin => ({ import: 'warning', restore: 'info' }[origin] || 'primary')

// Date SQL in UTC senza fuso: vedi SkillsEditor
const formatDateTime = (iso) => {
  if (!iso) return ''
  const d = asDate(iso)
  return d && !isNaN(d.getTime()) ? d.toLocaleString() : iso
}

watch(() => [props.modelValue, props.skillId], ([open]) => {
  if (!open) return
  selected.value = null
  preview.value = ''
  restoredMessage.value = ''
  versions.value = []
  load()
}, { immediate: true })
</script>

<style scoped>
.history-layout {
  display: grid;
  grid-template-columns: minmax(260px, 340px) 1fr;
  gap: 16px;
}
.history-list {
  min-width: 0;
}
.history-preview {
  min-width: 0;
}
.skill-source :deep(textarea) {
  font-family: Consolas, 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.45;
}
@media (max-width: 800px) {
  .history-layout {
    grid-template-columns: 1fr;
  }
}
</style>
